import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  FormControlLabel,
  Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UndoIcon from '@mui/icons-material/Undo';
import api from '../../services/api';
import { ReturnDialog } from '../../components/Evaluation/ReturnDialog';
import { ReturnCommentHistory } from '../../components/Evaluation/ReturnCommentHistory';
import {
  GoalReferencePanel,
  type EvaluationGoalReference,
} from '../../components/Evaluation/GoalReferencePanel';
import {
  ItemGoalReference,
  hasAnyItemGoal,
} from '../../components/Evaluation/ItemGoalReference';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import { formatDateJST, formatDateTimeJST } from '../../utils/dateUtils';
import {
  getStatusLabel,
  canReject,
  type EvaluationStatus,
  type GoalApprovalStatus
} from '../../utils/statusLabels';

interface Employee {
  employee_id: string;
  employee_name: string;
  email: string;
  hire_date: string;
  department_id: string;
  department_name: string;
  department_type: string;
  job_grade: string;
  job_title: string;
  rank: string;
  rank_cost: number;
  monthly_cost: number;
}

interface Evaluation {
  evaluation_id: string;
  employee_id: string;
  evaluation_period: string;
  department_type: string;
  status: string;
  allow_out_of_period_edit?: boolean;
  performance_score: number | null;
  salary_increase_rate: number | null;
  annual_sales: number | null;
  current_rank_cost: number | null;
  team_member_count: number | null;
  domestic_member_count: number | null;
  offshore_member_count: number | null;
  quantitative_dp_value: number | null;
  quantitative_dp_score: number | null;
  hr_approved_by: string | null;
  hr_approved_at: string | null;
  hr_comment: string | null;
  created_at: string;
  updated_at: string;
}

interface EvaluationItem {
  item_detail_id: string;
  item_id: string;
  category_number: number;
  category_name: string;
  item_number: string;
  item_description: string;
  weight: number;
  target_description: string;
  achievement_criteria: string | null;
  first_evaluation_score: number | null;
  first_evaluation_comment: string | null;
  second_evaluation_score: number | null;
  second_evaluation_comment: string | null;
  third_evaluation_score: number | null;
  third_evaluation_comment: string | null;
  final_evaluation_score: number | null;
}

interface SalesMetrics {
  target_expected_order: number;
  target_sales: number;
  target_gross_profit: number;
  actual_expected_order: number;
  actual_sales: number;
  actual_gross_profit: number;
}

interface Evaluator {
  evaluation_category: string;
  evaluator_id: string;
  evaluator_name: string;
  evaluation_weight: number;
}

interface EvaluatorItemScore {
  evaluator_id: string;
  evaluation_category: string;
  item_id: string;
  evaluation_score: number;
  evaluation_comment?: string;
  evaluator_name: string;
  evaluation_weight: number;
}

const HREvaluationDetailPage: React.FC = () => {
  const { employeeId, periodId } = useParams<{ employeeId: string; periodId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [evaluationItems, setEvaluationItems] = useState<EvaluationItem[]>([]);
  const [salesMetrics, setSalesMetrics] = useState<SalesMetrics | null>(null);
  const [evaluators, setEvaluators] = useState<Evaluator[]>([]);
  const [evaluatorItemScores, setEvaluatorItemScores] = useState<EvaluatorItemScore[]>([]);
  // 上長評価無効ルート（部門長が上長役を兼務）。ステータス表示の次アクション判定に使う
  const [managerEvalDisabled, setManagerEvalDisabled] = useState(false);
  const [goal, setGoal] = useState<EvaluationGoalReference | null>(null);

  // Approval dialog state
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalComment, setApprovalComment] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Return dialog state
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);

  // Alert state
  const [successAlert, setSuccessAlert] = useState(false);
  const [errorAlert, setErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const [savingOutOfPeriod, setSavingOutOfPeriod] = useState(false);

  useEffect(() => {
    fetchEvaluationDetail();
  }, [employeeId, periodId]);

  const handleToggleOutOfPeriod = async (allow: boolean) => {
    try {
      setSavingOutOfPeriod(true);
      const res = await api.patch(
        `/hr/evaluations/${employeeId}/${periodId}/out-of-period-edit`,
        { allow }
      );
      if (res.data.success) {
        setAlertMessage(res.data.message || '更新しました');
        setSuccessAlert(true);
        await fetchEvaluationDetail();
      }
    } catch (err: any) {
      setAlertMessage(err.response?.data?.message || '期間外操作許可の更新に失敗しました');
      setErrorAlert(true);
    } finally {
      setSavingOutOfPeriod(false);
    }
  };

  const fetchEvaluationDetail = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.get(
        `/hr/evaluations/${employeeId}/${periodId}`
      );

      if (response.data.success) {
        const {
          employee,
          evaluation,
          evaluation_items,
          sales_metrics,
          evaluators,
          evaluator_item_scores,
          manager_eval_disabled,
          goal: goalData,
        } = response.data.data;
        setEmployee(employee);
        setEvaluation(evaluation);
        setEvaluationItems(evaluation_items || []);
        setSalesMetrics(sales_metrics);
        setEvaluators(evaluators || []);
        setEvaluatorItemScores(evaluator_item_scores || []);
        setManagerEvalDisabled(manager_eval_disabled === true);
        setGoal(goalData ?? null);
      }
    } catch (err: any) {
      console.error('評価詳細取得エラー:', err);
      setError(err.response?.data?.message || '評価詳細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category: string) => {
    const categoryMap: Record<string, string> = {
      'self': '自己評価',
      'manager': '上長評価',
      'dept_head': '部門長評価',
    };
    return categoryMap[category] || category;
  };

  // 評価カテゴリ別の評価者を取得（評価ルートから）
  const getEvaluatorsByCategory = (category: string): Evaluator[] => {
    return evaluators.filter(ev => ev.evaluation_category === category);
  };

  const managerEvaluators = getEvaluatorsByCategory('manager');
  const deptHeadEvaluators = getEvaluatorsByCategory('dept_head');

  // 特定の評価者の項目別スコアを取得
  const getEvaluatorScore = (evaluatorId: string, itemId: string): string => {
    const itemScore = evaluatorItemScores.find(
      eis => eis.evaluator_id === evaluatorId && eis.item_id === itemId
    );
    if (itemScore && itemScore.evaluation_score !== null && itemScore.evaluation_score !== undefined) {
      return formatNumber(itemScore.evaluation_score, 0);
    }
    return '−';
  };

  const formatNumber = (value: any, decimals: number = 2): string => {
    if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
      return '−';
    }
    return Number(value).toFixed(decimals);
  };

  const calculateAchievementRate = (actual: number | null | undefined, target: number | null | undefined): string => {
    if (!actual || !target || target === 0) {
      return '−';
    }
    return ((actual / target) * 100).toFixed(2) + '%';
  };

  const handleApprovalDialogOpen = () => {
    setApprovalDialogOpen(true);
  };

  const handleApprovalDialogClose = () => {
    setApprovalDialogOpen(false);
    setApprovalComment('');
  };

  const handleApprove = async () => {
    if (!evaluation) return;

    try {
      setApprovalLoading(true);

      const response = await api.post(
        `/hr/evaluations/${evaluation.evaluation_id}/approve`,
        { comment: approvalComment }
      );

      if (response.data.success) {
        setAlertMessage('評価を承認しました');
        setSuccessAlert(true);
        handleApprovalDialogClose();
        // Refresh data
        await fetchEvaluationDetail();
      }
    } catch (err: any) {
      console.error('評価承認エラー:', err);
      setAlertMessage(err.response?.data?.message || '評価の承認に失敗しました');
      setErrorAlert(true);
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleReturn = async (comment: string) => {
    if (!evaluation) return;

    try {
      const response = await api.post(
        `/evaluations/${evaluation.evaluation_id}/reject`,
        { comment }
      );

      if (response.data.success) {
        setAlertMessage('評価を差し戻しました');
        setSuccessAlert(true);
        setReturnDialogOpen(false);
        // Refresh data
        await fetchEvaluationDetail();
      }
    } catch (err: any) {
      console.error('差し戻しエラー:', err);
      setAlertMessage(err.response?.data?.message || '評価の差し戻しに失敗しました');
      setErrorAlert(true);
    }
  };

  const canReturnEvaluation = () => {
    // HRロールはdept_completed（部門長評価完了）の場合のみ差し戻し可能
    return evaluation?.status ? canReject(evaluation.status as EvaluationStatus, 'hr') : false;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>読み込み中...</Typography>
      </Container>
    );
  }

  if (error || !employee) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error || '従業員が見つかりません'}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/hr/evaluations')}
          sx={{ mt: 2 }}
        >
          一覧に戻る
        </Button>
      </Container>
    );
  }

  const statusInfo = evaluation
    ? getStatusLabel(evaluation.status as EvaluationStatus, goal?.approval_status as GoalApprovalStatus, {
        managerEvalDisabled
      })
    : getStatusLabel(null);

  // カテゴリ別にグループ化
  const groupedItems = evaluationItems.reduce((acc, item) => {
    if (!acc[item.category_number]) {
      acc[item.category_number] = {
        category_name: item.category_name,
        items: []
      };
    }
    acc[item.category_number].items.push(item);
    return acc;
  }, {} as Record<number, { category_name: string; items: EvaluationItem[] }>);

  // 各カテゴリ内の項目を項目番号順にソート
  Object.keys(groupedItems).forEach(categoryNum => {
    groupedItems[Number(categoryNum)].items.sort((a, b) => {
      // item_numberから数値部分を抽出してソート (例: "B-10" -> 10)
      const aNum = parseInt(a.item_number.replace(/[^\d]/g, ''));
      const bNum = parseInt(b.item_number.replace(/[^\d]/g, ''));
      return aNum - bNum;
    });
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/hr/evaluations')}
        sx={{ mb: 2 }}
      >
        一覧に戻る
      </Button>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" gutterBottom>
            評価詳細（閲覧専用）
          </Typography>
          {evaluation && (
            <FormControlLabel
              control={
                <Switch
                  checked={evaluation.allow_out_of_period_edit === true}
                  disabled={savingOutOfPeriod}
                  onChange={(e) => handleToggleOutOfPeriod(e.target.checked)}
                />
              }
              label="期間外操作を許可"
            />
          )}
        </Box>
        {evaluation?.allow_out_of_period_edit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            この社員は期間外でも各フェーズの操作が可能です（前フェーズ完了の前提は維持されます）。
          </Alert>
        )}

        <GoalReferencePanel
          goal={goal}
          title="目標の申請状況"
          itemGoalsShown={hasAnyItemGoal(evaluationItems)}
        />

        {/* 従業員基本情報 */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              従業員情報
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">社員ID</Typography>
                <Typography variant="body1">{employee.employee_id}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">氏名</Typography>
                <Typography variant="body1">{employee.employee_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">メールアドレス</Typography>
                <Typography variant="body1">{employee.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">入社日</Typography>
                <Typography variant="body1">{formatDateJST(employee.hire_date)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">部門</Typography>
                <Typography variant="body1">{employee.department_name}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">職級</Typography>
                <Typography variant="body1">{employee.job_grade}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">役職</Typography>
                <Typography variant="body1">{employee.job_title}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">ランク</Typography>
                <Typography variant="body1">{employee.rank}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">年額ランクコスト</Typography>
                <Typography variant="body1">{formatCurrencyWithUnit(employee.rank_cost)}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 評価情報 */}
        {evaluation ? (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  評価結果
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">評価期間</Typography>
                    <Typography variant="body1">{evaluation.evaluation_period}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">ステータス</Typography>
                    <Chip label={statusInfo.label} color={statusInfo.chipColor} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">パフォーマンス評価点（P）</Typography>
                    <Typography variant="h5" color="primary">
                      {formatNumber(evaluation.performance_score)}点
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">昇給率</Typography>
                    <Typography variant="h5" color="secondary">
                      {formatNumber(evaluation.salary_increase_rate)}%
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Return Comment History */}
            {evaluation.evaluation_id && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    差し戻し履歴
                  </Typography>
                  <ReturnCommentHistory
                    evaluationId={evaluation.evaluation_id}
                    showAll={true}
                  />
                </CardContent>
              </Card>
            )}

            {/* HR Approval Section */}
            {evaluation.status !== 'finalized' ? (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    評価の最終承認
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    評価内容を確認し、承認または差し戻しを行ってください。
                  </Typography>
                  {evaluation.status !== 'r_applied' && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      R値適用が完了していないため、承認できません。
                    </Alert>
                  )}
                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={handleApprovalDialogOpen}
                      disabled={evaluation.status !== 'r_applied'}
                    >
                      承認する
                    </Button>
                    {canReturnEvaluation() && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<UndoIcon />}
                        onClick={() => setReturnDialogOpen(true)}
                      >
                        差し戻す
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ mb: 3, backgroundColor: '#f0f8f0' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="success.main">
                    承認済み
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">承認者</Typography>
                      <Typography variant="body1">{evaluation.hr_approved_by || '−'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Typography variant="body2" color="text.secondary">承認日時</Typography>
                      <Typography variant="body1">
                        {evaluation.hr_approved_at
                          ? formatDateTimeJST(evaluation.hr_approved_at, true)
                          : '−'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">承認コメント</Typography>
                      <Typography variant="body1">{evaluation.hr_comment || '（コメントなし）'}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* 評価者情報 */}
            {evaluators.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    評価者情報
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>評価区分</TableCell>
                          <TableCell>評価者</TableCell>
                          <TableCell align="right">評価重み</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {evaluators.map((evaluator, index) => (
                          <TableRow key={index}>
                            <TableCell>{getCategoryLabel(evaluator.evaluation_category)}</TableCell>
                            <TableCell>{evaluator.evaluator_name || evaluator.evaluator_id}</TableCell>
                            <TableCell align="right">{formatNumber(evaluator.evaluation_weight * 100, 0)}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* 営業指標（営業部門のみ） */}
            {employee.department_type === 'sales' && salesMetrics && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    営業指標
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>指標</TableCell>
                          <TableCell align="right">目標</TableCell>
                          <TableCell align="right">実績</TableCell>
                          <TableCell align="right">達成率</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>期待受注額</TableCell>
                          <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.target_expected_order)}</TableCell>
                          <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.actual_expected_order)}</TableCell>
                          <TableCell align="right">
                            {calculateAchievementRate(salesMetrics.actual_expected_order, salesMetrics.target_expected_order)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>売上高</TableCell>
                          <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.target_sales)}</TableCell>
                          <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.actual_sales)}</TableCell>
                          <TableCell align="right">
                            {calculateAchievementRate(salesMetrics.actual_sales, salesMetrics.target_sales)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>受注粗利額</TableCell>
                          <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.target_gross_profit)}</TableCell>
                          <TableCell align="right">{formatCurrencyWithUnit(salesMetrics.actual_gross_profit)}</TableCell>
                          <TableCell align="right">
                            {calculateAchievementRate(salesMetrics.actual_gross_profit, salesMetrics.target_gross_profit)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* 技術部門の定量データ */}
            {employee.department_type === 'tech' && evaluation.annual_sales !== null && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    技術部門定量データ
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">年間売上</Typography>
                      <Typography variant="body1">{formatCurrencyWithUnit(evaluation.annual_sales)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">評価時年額ランクコスト</Typography>
                      <Typography variant="body1">{formatCurrencyWithUnit(evaluation.current_rank_cost)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">チームメンバー数</Typography>
                      <Typography variant="body1">{evaluation.team_member_count || 0}名</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Dp値</Typography>
                      <Typography variant="body1">{formatNumber(evaluation.quantitative_dp_value, 4)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Typography variant="body2" color="text.secondary">Dp評価点</Typography>
                      <Typography variant="body1">{formatNumber(evaluation.quantitative_dp_score)}点</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}

            {/* 評価項目詳細 */}
            {evaluationItems.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    評価項目詳細
                  </Typography>
                  {Object.keys(groupedItems)
                    .sort((a, b) => Number(a) - Number(b))
                    .map((categoryNum) => {
                      const category = groupedItems[Number(categoryNum)];
                      return (
                        <Box key={categoryNum} sx={{ mb: 3 }}>
                          <Typography variant="subtitle1" color="primary" gutterBottom>
                            評価観点{categoryNum}: {category.category_name}
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ width: '80px' }}>項目No</TableCell>
                                  <TableCell>評価項目</TableCell>
                                  <TableCell align="center" sx={{ width: '80px' }}>重み</TableCell>
                                  <TableCell align="center" sx={{ width: '100px' }}>自己評価</TableCell>
                                  {/* 複数の上長評価者のカラムを動的に生成 */}
                                  {managerEvaluators.map((evaluator) => (
                                    <TableCell key={evaluator.evaluator_id} align="center" sx={{ width: '160px' }}>
                                      上長評価<br />
                                      <Typography variant="caption" component="span">
                                        （{evaluator.evaluator_name || evaluator.evaluator_id} | {formatNumber(evaluator.evaluation_weight * 100, 0)}%）
                                      </Typography>
                                    </TableCell>
                                  ))}
                                  {/* 複数の部門長評価者のカラムを動的に生成 */}
                                  {deptHeadEvaluators.map((evaluator) => (
                                    <TableCell key={evaluator.evaluator_id} align="center" sx={{ width: '160px' }}>
                                      部門長評価<br />
                                      <Typography variant="caption" component="span">
                                        （{evaluator.evaluator_name || evaluator.evaluator_id} | {formatNumber(evaluator.evaluation_weight * 100, 0)}%）
                                      </Typography>
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {category.items.map((item) => (
                                  <TableRow key={item.item_detail_id}>
                                    <TableCell>{item.item_number}</TableCell>
                                    <TableCell>
                                      {item.item_description}
                                      <ItemGoalReference
                                        targetDescription={item.target_description}
                                        achievementCriteria={item.achievement_criteria}
                                        title="この項目の目標（確定）"
                                      />
                                    </TableCell>
                                    <TableCell align="center">{item.weight}</TableCell>
                                    <TableCell align="center">
                                      {formatNumber(item.first_evaluation_score, 0)}
                                    </TableCell>
                                    {/* 複数の上長評価者のスコアを動的に表示 */}
                                    {managerEvaluators.map((evaluator) => (
                                      <TableCell key={evaluator.evaluator_id} align="center">
                                        {getEvaluatorScore(evaluator.evaluator_id, item.item_id)}
                                      </TableCell>
                                    ))}
                                    {/* 複数の部門長評価者のスコアを動的に表示 */}
                                    {deptHeadEvaluators.map((evaluator) => (
                                      <TableCell key={evaluator.evaluator_id} align="center">
                                        {getEvaluatorScore(evaluator.evaluator_id, item.item_id)}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          <Divider sx={{ my: 2 }} />
                        </Box>
                      );
                    })}
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Alert severity="info">この従業員の評価データはまだ作成されていません</Alert>
        )}
      </Paper>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={handleApprovalDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>評価の最終承認</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="承認コメント"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApprovalDialogClose} disabled={approvalLoading}>
            キャンセル
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            disabled={approvalLoading}
          >
            {approvalLoading ? '処理中...' : '承認する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return Dialog */}
      <ReturnDialog
        open={returnDialogOpen}
        onClose={() => setReturnDialogOpen(false)}
        onSubmit={handleReturn}
        title="部門評価を差し戻す"
        targetName={employee?.employee_name}
        maxLength={1000}
      />

      {/* Success Alert */}
      <Snackbar
        open={successAlert}
        autoHideDuration={6000}
        onClose={() => setSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessAlert(false)} severity="success" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Error Alert */}
      <Snackbar
        open={errorAlert}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorAlert(false)} severity="error" sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HREvaluationDetailPage;
